// src/scripts/calendar/adapters/ModalAdapter.js
/**
 * ModalAdapter - Clean interface to ModalManager with essential error tracking
 * 
 * Responsibilities:
 * - Provide clean adapter interface to ModalManager
 * - Handle modal operations and event content generation
 * - Manage adapter lifecycle and modal state
 * - Essential error tracking for critical modal operation failures
 * 
 * Key Methods:
 * - initialize() - Adapter setup with error handling
 * - show()/hide() - Modal operations with error tracking
 * - showEventModal() - Event modal generation with error handling
 * - getRecentErrors() - Recent failure debugging
 * - hasCriticalIssues() - Quick error status check
 * - clearErrorHistory() - Reset error tracking
 * 
 * Error Handling:
 * - initialization: Setup and ModalManager creation failures
 * - modal-operation: Show/hide and content generation failures
 * - configuration: Missing dependencies and invalid state failures
 * 
 * Note: Minimal error tracking approach - focuses only on critical failures
 * that could break modal functionality or event display.
 */

import ModalManager from '../ui/ModalManager.js';
import ErrorHandler from '../../utils/ErrorHandler.js';
import ModalContentGenerator from '../ui/ModalContentGenerator.js';

export default class ModalAdapter {
  constructor(logger = null, componentRefs = {}) {
    // Essential error tracking setup
    this.errorHandler = new ErrorHandler({
      module: 'ModalAdapter',
      maxErrors: 15
    });

    this.logger = logger || console;
    this.modalManager = null;
    this.initialized = false;
    this.contentGenerator = new ModalContentGenerator();

    // Component references for dependency injection
    this._componentRefs = componentRefs;
  }

  /* ------------------------------------------------------------------ */
  /* Logging helpers                                                     */
  /* ------------------------------------------------------------------ */

  _info(...args) {
    this.logger?.info && this.logger.info(...args);
  }

  _warn(...args) {
    this.logger?.warn && this.logger.warn(...args);
  }

  _error(...args) {
    this.logger?.error && this.logger.error(...args);
  }

  /**
   * Initialize the adapter and modal manager
   */
  initialize() {
    // Idempotent init: if already initialized, treat as success
    if (this.initialized && this.modalManager) return true;

    try {
      this._info('Initializing ModalAdapter...');

      // Create the wrapped ModalManager with component refs
      this.modalManager = new ModalManager({
        componentRefs: {
          ...this._componentRefs,
          showEventModal: (event, dateText) => this.showEventModal(event, dateText),
          modalAdapter: this
        }
      });

      if (!this.modalManager) {
        this.errorHandler.handleFatalError('Failed to create ModalManager', null, {
          category: 'initialization',
          action: 'modal-manager-creation-failed'
        });
        return false;
      }

      // Initialize the modal manager
      const initSuccess = this.modalManager.initialize();

      if (!initSuccess) {
        this.errorHandler.handleError('ModalManager initialization failed', null, {
          category: 'initialization',
          action: 'modal-manager-init-failed'
        });
        this._warn('ModalManager initialization failed');
        return false;
      }

      this.initialized = true;

      this.errorHandler.logSuccess('ModalAdapter initialized successfully', {
        category: 'initialization',
        action: 'init-success'
      });

      this._info('✅ ModalAdapter initialized successfully');
      return true;

    } catch (error) {
      this.errorHandler.handleFatalError('Critical error during ModalAdapter initialization', error, {
        category: 'initialization',
        action: 'fatal-init-error'
      });
      this._warn('⚠️ ModalAdapter initialization failed:', error.message);
      return false;
    }
  }

  /**
   * Show modal with content
   */
  show(content) {
    try {
      if (!this.isAvailable()) {
        this.errorHandler.handleError('Modal not available for show operation', null, {
          category: 'configuration',
          action: 'modal-unavailable-show'
        });
        this._warn('Modal not available');
        return false;
      }

      return this.modalManager.show(content);
    } catch (error) {
      this.errorHandler.handleError('Error showing modal', error, {
        category: 'modal-operation',
        action: 'show-failed'
      });
      this._error('❌ Error showing modal:', error);
      return false;
    }
  }

  /**
   * Hide modal
   */
  hide() {
    try {
      if (!this.isAvailable()) {
        return false;
      }

      return this.modalManager.hide();
    } catch (error) {
      this.errorHandler.handleError('Error hiding modal', error, {
        category: 'modal-operation',
        action: 'hide-failed'
      });
      this._error('❌ Error hiding modal:', error);
      return false;
    }
  }

  /**
   * Check if modal is visible
   */
  isVisible() {
    try {
      if (!this.isAvailable()) {
        return false;
      }

      return this.modalManager.isVisible();
    } catch (error) {
      this.errorHandler.handleError('Error checking modal visibility', error, {
        category: 'modal-operation',
        action: 'visibility-check-failed'
      });
      this._error('❌ Error checking modal visibility:', error);
      return false;
    }
  }

  /**
   * Show event modal with formatted content
   */
  async showEventModal(event, dateText = '') {
    try {
      if (!this.isAvailable()) {
        this.errorHandler.handleError('Modal not available for event display', null, {
          category: 'configuration',
          action: 'modal-unavailable-event'
        });
        this._warn('Modal not available for event display');
        return false;
      }

      // Use centralized content generator (now async)
      const content = await this.contentGenerator.generateModalContent(event, dateText);

      if (!content) {
        this.errorHandler.handleError('Failed to generate event modal content', null, {
          category: 'modal-operation',
          action: 'content-generation-failed',
          hasEvent: !!event
        });
        return false;
      }

      return this.show(content);
    } catch (error) {
      this.errorHandler.handleError('Error showing event modal', error, {
        category: 'modal-operation',
        action: 'event-modal-failed',
        eventTitle: event?.title
      });
      this._error('❌ Error showing event modal:', error);
      return false;
    }
  }

  /**
   * Refresh list view handlers (if available)
   */
  refreshListViewHandlers() {
    try {
      if (!this.isAvailable()) {
        return false;
      }

      if (typeof this.modalManager.refreshListViewHandlers === 'function') {
        this.modalManager.refreshListViewHandlers();
        this._info('✅ List view handlers refreshed');
        return true;
      }

      return false;
    } catch (error) {
      this.errorHandler.handleError('Error refreshing list view handlers', error, {
        category: 'configuration',
        action: 'refresh-handlers-failed'
      });
      this._error('❌ Error refreshing list view handlers:', error);
      return false;
    }
  }

  /**
   * Get the wrapped modal manager (for advanced usage)
   */
  getModalManager() {
    return this.modalManager;
  }

  /**
   * Check if modal functionality is available
   */
  isAvailable() {
    return this.initialized && !!this.modalManager;
  }

  /**
   * Check if adapter is initialized
   */
  isInitialized() {
    return this.initialized;
  }

  /**
   * Update component references after construction (for late binding)
   * @param {Object} refs - Component references to update
   */
  setComponentRefs(refs = {}) {
    this._componentRefs = { ...this._componentRefs, ...refs };

    // Update ModalManager refs if already created
    if (this.modalManager) {
      this.modalManager.setComponentRefs(refs);
    }
  }

  /**
   * Get adapter status
   */
  getStatus() {
    return {
      initialized: this.initialized,
      hasModalManager: !!this.modalManager,
      hasContentGenerator: !!this.contentGenerator,
      isVisible: this.isVisible(),
      isAvailable: this.isAvailable()
    };
  }

  /**
   * Clean up and destroy
   */
  async destroy() {
    try {
      this._info('🗑️ Destroying ModalAdapter...');

      // Hide modal if visible
      if (this.isVisible()) {
        this.hide();
      }

      if (this.modalManager && typeof this.modalManager.destroy === 'function') {
        await this.modalManager.destroy();
      }

      this.modalManager = null;
      this.contentGenerator = null;
      this.initialized = false;

      this.errorHandler.logSuccess('ModalAdapter destroyed successfully', {
        category: 'initialization',
        action: 'destroy-success'
      });

      this._info('✅ ModalAdapter destroyed');
    } catch (error) {
      this.errorHandler.handleError('Error during ModalAdapter destruction', error, {
        category: 'initialization',
        action: 'destroy-failed'
      });
      this._error('❌ Error destroying ModalAdapter:', error);
    }
  }

  /**
   * Get recent errors for debugging
   * @param {number} minutes - Minutes to look back (default: 5)
   * @returns {Array} Recent error entries
   */
  getRecentErrors(minutes = 5) {
    return this.errorHandler.getRecentErrors(minutes);
  }

  /**
   * Check if there are critical issues that need attention
   * @returns {boolean} True if critical issues exist
   */
  hasCriticalIssues() {
    return this.errorHandler.hasCriticalIssues();
  }

  /**
   * Clear error history (useful for testing or after resolving issues)
   */
  clearErrorHistory() {
    this.errorHandler.clearErrorHistory();
  }

  /**
   * Get comprehensive stats including error information
   * @returns {Object} Stats object with error data
   */
  getAdapterStats() {
    const errorStats = this.errorHandler.getErrorStats();
    const status = this.getStatus();

    return {
      ...status,
      errors: {
        total: errorStats.totalErrors,
        fatal: errorStats.fatalErrors,
        recent: errorStats.recentErrors,
        byCategory: errorStats.errorsByCategory
      }
    };
  }
}
